/**
 * Public privacy policy — required for Google OAuth verification (Gmail readonly).
 * URL: https://deeplysmart.app/privacy
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container max-w-3xl py-12 px-4 prose prose-sm dark:prose-invert">
        <h1>Deeply Smart Privacy Policy</h1>
        <p className="text-muted-foreground text-sm">Last updated: June 2026</p>

        <h2>Overview</h2>
        <p>
          Deeply Smart (&quot;we&quot;, &quot;our&quot;) provides an AI-powered workspace at deeplysmart.app.
          This policy describes how we handle information when you use our services, including optional
          third-party integrations you choose to connect.
        </p>

        <h2>Google Gmail integration</h2>
        <p>
          If you connect Google (Library → Google Drive or Communication → Gmail), you authorize Deeply Smart
          to access your Google account using Drive and/or <strong>gmail.readonly</strong> scopes. Gmail access is used only to:
        </p>
        <ul>
          <li>Display your inbox, sent, and starred messages in the Communication inbox</li>
          <li>Let you optionally run AI analysis on messages you select (processed in your session; not stored as permanent Gmail archives by default)</li>
        </ul>
        <p>We do <strong>not</strong> use Gmail access to send, delete, or modify your email.</p>

        <h2>Data we store</h2>
        <ul>
          <li>
            <strong>OAuth tokens</strong> — stored in your browser (localStorage) to maintain your Google
            connection. Cleared when you disconnect in the app or sign out of Google.
          </li>
          <li>
            <strong>Account email</strong> — cached locally with the connection to show which account is connected.
          </li>
          <li>
            <strong>Message content</strong> — fetched from Gmail when you view or analyze messages; not written to a
            separate long-term email archive unless you explicitly save output elsewhere in the product.
          </li>
        </ul>

        <h2>Data sharing</h2>
        <p>
          We do not sell your Gmail data. Message content may be sent to AI providers you invoke (e.g. via your
          selected AI agent) only when you choose to analyze a message. Gmail and Drive API calls are made directly
          from your browser using your OAuth access token.
        </p>

        <h2>Revoking access</h2>
        <p>
          Disconnect Gmail in Deeply Smart (Communication → Gmail → Disconnect), or revoke access anytime in your{" "}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
            Google Account permissions
          </a>
          .
        </p>

        <h2>Contact</h2>
        <p>
          Questions:{" "}
          <a href="mailto:support@deeplysmart.io">support@deeplysmart.io</a>
        </p>

        <p className="text-sm text-muted-foreground pt-8">
          <a href="/">← Back to Deeply Smart</a>
        </p>
      </div>
    </div>
  );
}
